/**
 * Provider Registry — النقطة الوحيدة التي يُختار فيها مصدر البيانات.
 *
 * الافتراضي من PHASE 2A: **ProductionMarketDataProvider**.
 * `DemoMarketDataProvider` لم يعد يُستخدم في تجربة المستخدم — يبقى للاختبارات
 * والتطوير فقط، ولا يُفعَّل إلا بـ`VITE_DATA_SOURCE=demo` صراحة.
 *
 * ممنوع السقوط التلقائي من LIVE إلى DEMO: غياب البيانات الحقيقية يُعرض كما هو.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { MarketDataProvider } from '@/domain/market-data-provider';
import { DemoMarketDataProvider, type DemoScenario } from './demo/DemoMarketDataProvider';
import { ProductionMarketDataProvider } from './production/ProductionMarketDataProvider';

export { DemoMarketDataProvider, ProductionMarketDataProvider };
export type { DemoScenario };

export type DataSourceMode = 'LIVE' | 'DEMO';

interface ProviderContextValue {
  provider: MarketDataProvider;
  origin: 'demo' | 'production';
  /** مؤشر واضح للتطوير: LIVE أو DEMO. */
  mode: DataSourceMode;
  scenario: DemoScenario | null;
  setScenario: (s: DemoScenario) => void;
}

const MarketDataContext = createContext<ProviderContextValue | null>(null);

function defaultProvider(): MarketDataProvider {
  const configured = import.meta.env.VITE_DATA_SOURCE;
  if (configured === 'demo') return new DemoMarketDataProvider();
  return new ProductionMarketDataProvider();
}

export interface MarketDataProviderScopeProps {
  children: ReactNode;
  /** حقن Provider بديل — تُستخدم في الاختبارات. */
  provider?: MarketDataProvider;
}

export function MarketDataProviderScope({
  children,
  provider,
}: MarketDataProviderScopeProps): JSX.Element {
  const instance = useMemo<MarketDataProvider>(() => provider ?? defaultProvider(), [provider]);
  const [scenario, setScenarioState] = useState<DemoScenario>('normal');

  const value = useMemo<ProviderContextValue>(() => {
    const isDemo = instance instanceof DemoMarketDataProvider;
    return {
      provider: instance,
      origin: instance.origin,
      mode: isDemo ? 'DEMO' : 'LIVE',
      scenario: isDemo ? scenario : null,
      setScenario: (s: DemoScenario) => {
        if (isDemo) {
          (instance as DemoMarketDataProvider).setScenario(s);
          setScenarioState(s);
        }
      },
    };
  }, [instance, scenario]);

  return <MarketDataContext.Provider value={value}>{children}</MarketDataContext.Provider>;
}

export function useMarketDataContext(): ProviderContextValue {
  const ctx = useContext(MarketDataContext);
  if (!ctx) {
    throw new Error('useMarketDataContext must be used inside <MarketDataProviderScope>.');
  }
  return ctx;
}

export function useMarketData(): MarketDataProvider {
  return useMarketDataContext().provider;
}
